/**
 * Subscription tier definitions and helpers.
 *
 * This is the single source of truth for drug limits and monthly check
 * allowances. The tier IDs match the `subscription_tier` column on the
 * `accounts` table (`small`, `medium`, `large`).
 */

export type TierId = 'small' | 'medium' | 'large';

/** Number of distinct drug pairs for `n` drugs (n choose 2). */
export function pairsFor(n: number) {
  return (n * (n - 1)) / 2;
}

export interface TierDefinition {
  id: TierId;
  name: string;
  subtitle: string;
  price: number;
  /** Regular price shown crossed out next to `price` (promo) */
  originalPrice: number | null;
  maxDrugs: number;
  pairs: number;
  monthlyChecks: number | null; // null = unlimited
  highlighted: boolean;
  badge: string | null;
}

export const TIERS: TierDefinition[] = [
  {
    id: 'small',
    name: 'Podstawowy',
    subtitle: 'Dla indywidualnych konsultacji',
    price: 79,
    originalPrice: null,
    maxDrugs: 5,
    pairs: pairsFor(5),
    monthlyChecks: 10,
    highlighted: false,
    badge: null,
  },
  {
    id: 'medium',
    name: 'Premium',
    subtitle: 'Codzienna praca z pacjentami',
    price: 89,
    originalPrice: 189,
    maxDrugs: 8,
    pairs: pairsFor(8),
    monthlyChecks: 20,
    highlighted: true,
    badge: 'Popularny',
  },
  {
    id: 'large',
    name: 'Platinum',
    subtitle: 'Badanie kliniczne, przeglądy lekowe',
    price: 1799,
    originalPrice: null,
    maxDrugs: 15,
    pairs: pairsFor(15),
    monthlyChecks: 250,
    highlighted: false,
    badge: null,
  },
];

const VALID_TIER_IDS = new Set<string>(TIERS.map((t) => t.id));

/** Narrow a raw DB string to a valid TierId (or null). */
export function toTierId(value: string | null | undefined): TierId | null {
  if (!value || !VALID_TIER_IDS.has(value)) return null;
  return value as TierId;
}

/** Absolute ceiling — custom plans max (see custom_plans.max_drugs check). */
export const MAX_DRUGS_GLOBAL = 20;

/** Tier whose limits apply to anonymous and trial users (Premium). */
const DEFAULT_TIER_ID: TierId = 'medium';

/**
 * Return the drug limit for a given tier ID.
 * - `null` / `undefined` → trial/anonymous defaults (Premium tier's limit)
 * - known tier → tier's maxDrugs
 */
export function getMaxDrugsForTier(tierId: TierId | null | undefined): number {
  const tier =
    TIERS.find((t) => t.id === tierId) ??
    TIERS.find((t) => t.id === DEFAULT_TIER_ID);

  return tier?.maxDrugs ?? MAX_DRUGS_GLOBAL;
}
